import { useCallback, useEffect, useRef, useState } from 'react';

type FeedMode = 'live' | 'game';
export const FEED_STORAGE_KEY = 'runordie.feed';

function savedFeed(fallback: FeedMode): FeedMode {
  try {
    const value = window.localStorage.getItem(FEED_STORAGE_KEY);
    return value === 'live' || value === 'game' ? value : fallback;
  } catch {
    return fallback;
  }
}

/** Let the thumb move before replacing the empty screen; no page transition. */
export function useFeedSwitch(initial: FeedMode, inArena: boolean) {
  const [feed, setFeed] = useState(() => savedFeed(initial));
  const [selectedFeed, setSelectedFeed] = useState(feed);
  const current = useRef(feed);
  const requested = useRef(feed);
  const timer = useRef<number | undefined>(undefined);
  const commit = useCallback((next: FeedMode) => {
    current.current = next;
    setFeed(next);
  }, []);
  const changeFeed = useCallback(
    (next: FeedMode) => {
      window.clearTimeout(timer.current);
      requested.current = next;
      setSelectedFeed(next);
      // Persist the user's intent before the animation, even if they reload now.
      try {
        window.localStorage.setItem(FEED_STORAGE_KEY, next);
      } catch {
        // Disabled/full storage must not prevent switching within this session.
      }
      if (
        next === current.current ||
        !inArena ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        commit(next);
      } else {
        timer.current = window.setTimeout(() => commit(next), 180);
      }
    },
    [commit, inArena],
  );
  useEffect(() => {
    if (!inArena) {
      window.clearTimeout(timer.current);
      commit(requested.current);
    }
  }, [commit, inArena]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return { feed, selectedFeed, changeFeed };
}
